import { Fragment } from "react";
import {
  Database,
  BrainCircuit,
  Gauge,
  UserCheck,
  TrendingUp,
  ChevronDown,
  Lock,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionShell, SectionHead, IconChip } from "@/components/site/Primitives";

/* ---------------------------------------------------------------------
   Simplified public architecture — five-tier executive flow.
   Detailed proprietary sub-architecture is intentionally not exposed.
--------------------------------------------------------------------- */

const INFO_INPUTS = [
  "Contracts",
  "Operations",
  "Financial Data",
  "Market Intelligence",
  "Regulation",
];

const KOS_CAPABILITIES = [
  "Commercial knowledge integration",
  "Commercial & financial reasoning",
  "Scenario intelligence",
];

const EDI_ATTRIBUTES = [
  "Exposure",
  "Options",
  "Recommendation",
  "Confidence",
  "Evidence",
];

function TierArrow() {
  return (
    <div className="my-4 flex justify-center sm:my-5">
      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-natsu-navy/15 bg-white text-natsu-slate/70 shadow-[0_6px_20px_-12px_rgba(10,23,41,0.35)]">
        <ChevronDown className="h-4 w-4" strokeWidth={1.8} />
      </span>
    </div>
  );
}

function Chip({ children, tone = "slate" }) {
  const tones = {
    slate:
      "bg-white text-natsu-navy ring-1 ring-natsu-navy/12",
    onDark:
      "bg-white/[0.06] text-natsu-warm-white ring-1 ring-white/15",
    gold:
      "bg-white text-natsu-navy ring-1 ring-natsu-gold/40",
    purple:
      "bg-white text-natsu-navy ring-1 ring-natsu-purple/30",
    green:
      "bg-white text-natsu-navy ring-1 ring-natsu-green/30",
  };
  return (
    <span
      className={`whitespace-nowrap rounded-lg px-3.5 py-1.5 text-[12.5px] font-medium sm:text-[13px] ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function TierCard({
  eyebrow,
  eyebrowColor = "gold",
  title,
  titleNote,
  icon,
  tint = "blue",
  tone = "light",
  children,
  className = "",
  testId,
}) {
  const isDark = tone === "dark";
  const wrapperTone = isDark
    ? "bg-natsu-navy on-dark ring-1 ring-white/12"
    : "bg-white ring-1 ring-natsu-navy/12 shadow-[0_18px_44px_-28px_rgba(10,23,41,0.28)]";
  const titleColor = isDark ? "text-natsu-warm-white" : "text-natsu-navy";
  const eyebrowColorCls = {
    gold: "text-natsu-gold",
    blue: "text-natsu-blue",
    purple: "text-natsu-purple",
    green: "text-natsu-green",
    slate: isDark ? "text-natsu-warm-white/60" : "text-natsu-slate",
  }[eyebrowColor];

  return (
    <div
      data-testid={testId}
      className={`mx-auto w-full max-w-[860px] rounded-2xl px-6 py-6 sm:px-8 sm:py-7 ${wrapperTone} ${className}`}
    >
      <div className="flex items-center gap-3.5">
        {icon && <IconChip icon={icon} tint={tint} tone={isDark ? "dark" : "light"} />}
        <div className="min-w-0">
          {eyebrow && (
            <p
              className={`text-[10.5px] font-semibold uppercase tracking-[0.18em] ${eyebrowColorCls}`}
            >
              {eyebrow}
            </p>
          )}
          <h3
            className={`mt-1 font-display text-[19px] leading-snug sm:text-[22px] ${titleColor}`}
          >
            {title}
          </h3>
          {titleNote && (
            <p
              className={`mt-1 text-[13px] leading-relaxed ${
                isDark ? "text-natsu-warm-white/70" : "text-natsu-slate"
              }`}
            >
              {titleNote}
            </p>
          )}
        </div>
      </div>
      {children && <div className="mt-5">{children}</div>}
    </div>
  );
}

export function Architecture() {
  return (
    <SectionShell id="architecture" tone="mist">
      <div className="max-w-3xl">
        <SectionHead
          label="How Everything Connects"
          labelColor="blue"
          title="From source information to governed commercial decisions."
          intro="Enterprise, maritime and market information flows through the NATSU Commercial Knowledge Operating System™ to produce Executive Decision Intelligence™—supporting governed human action and measurable commercial outcomes."
        />
      </div>

      {/* ---------------------------------------------------------------
         Five-tier institutional architecture (executive-level view)
      --------------------------------------------------------------- */}
      <div className="mt-14 sm:mt-16">
        {/* Tier 1 — Information inputs */}
        <Reveal>
          <TierCard
            eyebrow="Information"
            eyebrowColor="slate"
            title="Enterprise + Maritime + Market Information"
            icon={Database}
            tint="blue"
            testId="arch-tier-inputs"
          >
            <div className="flex flex-wrap gap-2">
              {INFO_INPUTS.map((label, i) => (
                <Fragment key={label}>
                  <Chip>{label}</Chip>
                  {i < INFO_INPUTS.length - 1 && (
                    <span className="hidden select-none items-center text-[13px] text-natsu-slate/50 sm:inline-flex">
                      ·
                    </span>
                  )}
                </Fragment>
              ))}
            </div>
          </TierCard>
        </Reveal>

        <Reveal>
          <TierArrow />
        </Reveal>

        {/* Tier 2 — Operating System (proprietary backend, restrained public view) */}
        <Reveal>
          <TierCard
            eyebrow="Proprietary Backend"
            eyebrowColor="gold"
            title="NATSU Commercial Knowledge Operating System™"
            icon={BrainCircuit}
            tint="gold"
            tone="dark"
            testId="arch-tier-kos"
          >
            <div className="flex flex-wrap gap-2">
              {KOS_CAPABILITIES.map((label) => (
                <span
                  key={label}
                  className="whitespace-nowrap rounded-lg bg-white/[0.06] px-3.5 py-1.5 text-[12.5px] font-medium text-natsu-warm-white/90 ring-1 ring-white/15 sm:text-[13px]"
                >
                  {label}
                </span>
              ))}
            </div>
            <p className="mt-4 max-w-[64ch] text-[13px] leading-relaxed text-natsu-warm-white/65">
              Commercial knowledge · reasoning · scenario intelligence · evidence.
            </p>
          </TierCard>
        </Reveal>

        <Reveal>
          <TierArrow />
        </Reveal>

        {/* Tier 3 — Executive Decision Intelligence */}
        <Reveal>
          <TierCard
            eyebrow="Customer Outcome"
            eyebrowColor="gold"
            title="Executive Decision Intelligence™"
            icon={Gauge}
            tint="gold"
            testId="arch-tier-edi"
          >
            <div className="flex flex-wrap gap-2">
              {EDI_ATTRIBUTES.map((label) => (
                <Chip key={label} tone="gold">
                  {label}
                </Chip>
              ))}
            </div>
          </TierCard>
        </Reveal>

        <Reveal>
          <TierArrow />
        </Reveal>

        {/* Tier 4 — Human Review & Action */}
        <Reveal>
          <TierCard
            eyebrow="Governed Action"
            eyebrowColor="purple"
            title="Human Review & Action"
            titleNote="Authorised decision-makers review, approve, modify, reject or escalate."
            icon={UserCheck}
            tint="purple"
            testId="arch-tier-human"
          />
        </Reveal>

        <Reveal>
          <TierArrow />
        </Reveal>

        {/* Tier 5 — Commercial Outcome */}
        <Reveal>
          <TierCard
            eyebrow="Business Result"
            eyebrowColor="green"
            title="Commercial Outcome"
            titleNote="Clearer decisions · faster action · lower risk · stronger performance."
            icon={TrendingUp}
            tint="green"
            testId="arch-tier-outcome"
          />
        </Reveal>
      </div>

      {/* ---------------------------------------------------------------
         Restrained closing: the customer-specific memory loop.
      --------------------------------------------------------------- */}
      <Reveal>
        <div className="mx-auto mt-14 w-full max-w-[860px] rounded-2xl border border-natsu-purple/25 bg-natsu-purple/[0.05] p-6 sm:p-7">
          <div className="flex items-start gap-3.5">
            <IconChip icon={Lock} tint="purple" />
            <div>
              <h4 className="font-display text-[18px] leading-snug text-natsu-navy sm:text-[20px]">
                Customer-Specific Decision Memory™
              </h4>
              <p className="mt-3 max-w-[74ch] text-[14px] leading-relaxed text-natsu-navy/80">
                Approved outcomes and validated decisions strengthen future
                recommendations for that customer. Customer context remains
                permissioned, traceable and securely segregated between customer
                environments.
              </p>
            </div>
          </div>
        </div>
      </Reveal>
    </SectionShell>
  );
}
